package com.taocent.simple.compose.component.blockrichtext.internal.registry

import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.AnnotatedString
import com.taocent.simple.compose.component.blockrichtext.BlockState
import com.taocent.simple.compose.component.blockrichtext.DocumentBlock
import kotlin.reflect.KClass

class BlockRegistry {

    private val renderers = mutableMapOf<KClass<out DocumentBlock>, BlockRenderer<*>>()
    private val serializers = mutableMapOf<KClass<out DocumentBlock>, BlockSerializer<*>>()
    private val serializersByTypeName = mutableMapOf<String, BlockSerializer<*>>()
    private val kindMap = mutableMapOf<KClass<out DocumentBlock>, BlockKind>()

    fun <T : DocumentBlock> registerRenderer(renderer: BlockRenderer<T>) {
        renderers[renderer.blockClass] = renderer
        kindMap[renderer.blockClass] = renderer.kind
    }

    fun <T : DocumentBlock> registerSerializer(serializer: BlockSerializer<T>) {
        serializers[serializer.blockClass] = serializer
        serializersByTypeName[serializer.typeName()] = serializer
    }

    fun rendererFor(block: DocumentBlock): BlockRenderer<*>? {
        return renderers[block::class]
    }

    fun serializerFor(block: DocumentBlock): BlockSerializer<*>? {
        return serializers[block::class]
    }

    fun serializerForTypeName(typeName: String): BlockSerializer<*>? {
        return serializersByTypeName[typeName]
    }

    fun kindOf(block: DocumentBlock): BlockKind? {
        return kindMap[block::class]
    }

    @Composable
    fun render(
        block: DocumentBlock,
        blockState: BlockState,
        modifier: Modifier
    ) {
        val renderer = rendererFor(block)
        if (renderer != null) {
            renderer.renderAny(block, blockState, modifier)
        }
    }

    fun toJson(block: DocumentBlock): String? {
        return serializerFor(block)?.toJsonAny(block)
    }

    fun toJson(block: DocumentBlock, contentResolver: ((DocumentBlock) -> AnnotatedString?)?): String? {
        val serializer = serializerFor(block)
        if (serializer == null) return null
        @Suppress("UNCHECKED_CAST")
        return (serializer as BlockSerializer<DocumentBlock>).toJson(block, contentResolver)
    }

    fun fromJson(typeName: String, root: Map<String, Any?>): DocumentBlock? {
        return serializerForTypeName(typeName)?.fromJson(root)
    }
}